import React, { useState } from 'react';

const RnDResultsSection = ({ extractedText = '', llmResponse = '', filesProcessed = 0, isDataLoaded = false }) => {
  const [activeTab, setActiveTab] = useState('llm-response');

  // Function to detect and render tables in markdown
  const renderMarkdownContent = (content) => {
    if (!content) return null;

    // Split content by lines
    const lines = content.split('\n');
    const elements = [];
    let currentTable = [];
    let inTable = false;
    let currentParagraph = [];

    lines.forEach((line, index) => {
      // Check if line is a table row (contains |)
      if (line.trim().includes('|') && line.trim().split('|').length > 2) {
        // If we were building a paragraph, finish it
        if (currentParagraph.length > 0) {
          elements.push(
            <div key={`para-${index}`} className="mb-4">
              {currentParagraph.map((pLine, pIndex) => (
                <p key={pIndex} className="mb-2">{pLine}</p>
              ))}
            </div>
          );
          currentParagraph = [];
        }

        inTable = true;
        currentTable.push(line);
      } else {
        // If we were building a table, finish it
        if (inTable && currentTable.length > 0) {
          elements.push(renderTable(currentTable, `table-${index}`));
          currentTable = [];
          inTable = false;
        }

        // Add to current paragraph if not empty
        if (line.trim()) {
          currentParagraph.push(line);
        } else if (currentParagraph.length > 0) {
          // Empty line - finish current paragraph
          elements.push(
            <div key={`para-${index}`} className="mb-4">
              {currentParagraph.map((pLine, pIndex) => (
                <p key={pIndex} className="mb-2">{pLine}</p>
              ))}
            </div>
          );
          currentParagraph = [];
        }
      }
    });

    // Handle remaining content
    if (inTable && currentTable.length > 0) {
      elements.push(renderTable(currentTable, 'table-final'));
    }
    if (currentParagraph.length > 0) {
      elements.push(
        <div key="para-final" className="mb-4">
          {currentParagraph.map((pLine, pIndex) => (
            <p key={pIndex} className="mb-2">{pLine}</p>
          ))}
        </div>
      );
    }

    return elements.length > 0 ? elements : <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">{content}</p>;
  };

  const renderTable = (tableLines, key) => {
    if (tableLines.length < 2) return null;

    // Parse table
    const rows = tableLines.map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell !== '')
    );

    // Filter out separator rows (containing only - and |)
    const dataRows = rows.filter(row => 
      !row.every(cell => /^[-\s|]*$/.test(cell))
    );

    if (dataRows.length === 0) return null;

    const headers = dataRows[0];
    const bodyRows = dataRows.slice(1);

    return (
      <div key={key} className="mb-6 overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 transition-colors duration-300">
          <thead>
            <tr className="bg-gray-50">
              {headers.map((header, index) => (
                <th key={index} className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log(`${type} copied to clipboard`);
    });
  };

  if (!isDataLoaded) {
    return (
      <section className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white transition-colors duration-300">RnD Processing Results</h3>
          </div>
          <div className="px-6 py-16 text-center text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <div className="max-w-sm mx-auto">
              <div className="mb-4">
                <i className="fa-solid fa-flask text-6xl text-gray-300 dark:text-gray-600"></i>
              </div>
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                No Results Yet
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">
                Upload documents and process them with a custom prompt to see results here.
              </p>
              <div className="inline-flex items-center text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                <i className="fa-solid fa-magic-wand-sparkles mr-2"></i>
                <span>RnD prompting lab ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-2xl font-bold text-red-600">{filesProcessed}</div>
          <div className="text-sm text-gray-600">Files Processed</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-2xl font-bold text-green-600">{extractedText.length}</div>
          <div className="text-sm text-gray-600">Characters Extracted</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-2xl font-bold text-blue-600">{llmResponse.length}</div>
          <div className="text-sm text-gray-600">Response Length</div>
        </div>
      </div>

      {/* Results Tabs */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('llm-response')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'llm-response'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="fa-solid fa-robot mr-2"></i>
              LLM Response
            </button>
            <button
              onClick={() => setActiveTab('extracted-text')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'extracted-text'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="fa-solid fa-file-text mr-2"></i>
              Extracted Text
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'llm-response' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-800">
                  Gemini AI Response
                </h4>
                <button
                  onClick={() => copyToClipboard(llmResponse, 'LLM Response')}
                  className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md"
                  title="Copy to clipboard"
                >
                  <i className="fa-solid fa-copy mr-1"></i>
                  Copy
                </button>
              </div>
              <div className="prose max-w-none">
                {renderMarkdownContent(llmResponse)}
              </div>
            </div>
          )}

          {activeTab === 'extracted-text' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-800">
                  OCR Extracted Text
                </h4>
                <button
                  onClick={() => copyToClipboard(extractedText, 'Extracted Text')}
                  className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md"
                  title="Copy to clipboard"
                >
                  <i className="fa-solid fa-copy mr-1"></i>
                  Copy
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {extractedText}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Processed {filesProcessed} file(s) with custom prompt
          </div>
          <div className="flex space-x-2">
            <div className="inline-flex items-center text-xs text-red-600">
              <i className="fa-solid fa-magic-wand-sparkles mr-1"></i>
              <span>OCR + Gemini AI</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RnDResultsSection;
